/**
 * ProductInfo Component - Story 7.3: Create Product Detail Page
 * Displays comprehensive product information including name, category, price, and description
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCategoryMetadata } from '../config/categoryConfig';
import type { ProductInfoProps } from '../types/publicMarketplace.types';
import { ProductStatus, isInactiveStatus } from '../constants/statusConstants';

/**
 * ProductInfo Component
 * Displays detailed product information in a card layout
 *
 * Features:
 * - Product name and category badge
 * - Price display with unit
 * - Status badges (Price Negotiable, Out of Stock)
 * - Full description with read more/less toggle
 * - Inquiry count display
 * - Responsive design
 *
 * @component
 * @example
 * <ProductInfo product={productDetailData} />
 */
export const ProductInfo: React.FC<ProductInfoProps> = ({ product, className }) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const categoryMeta = getCategoryMetadata(product.category);

  // Check if description is long enough to need truncation
  const DESCRIPTION_TRUNCATE_LENGTH = 300;
  const shouldTruncate = product.description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const displayDescription = shouldTruncate && !isDescriptionExpanded
    ? `${product.description.substring(0, DESCRIPTION_TRUNCATE_LENGTH)}...`
    : product.description;

  const isOutOfStock = isInactiveStatus(product.status as ProductStatus);

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-6 space-y-6">
        {/* Product Name and Category */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-bold text-gray-900 leading-tight">
              {product.name}
            </h1>
          </div>

          {/* Category Badge */}
          <div className="flex items-center gap-2">
            <Badge
              className={cn(
                'text-sm font-medium',
                categoryMeta.badgeBgColor,
                categoryMeta.badgeColor
              )}
            >
              <categoryMeta.icon className="mr-1.5 h-4 w-4" />
              {categoryMeta.label}
            </Badge>

            {/* Out of Stock Badge */}
            {isOutOfStock && (
              <Badge variant="destructive" className="text-sm font-medium">
                <AlertCircle className="mr-1.5 h-4 w-4" />
                Out of Stock
              </Badge>
            )}
          </div>
        </div>

        {/* Price Section */}
        <div className="border-t border-b border-gray-200 py-4">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-primary">
              ₹{(product.price ?? 0).toLocaleString('en-IN')}
            </span>
            <span className="text-xl text-gray-600">/ {product.unit}</span>
          </div>

          {/* Price Negotiable Note - Only show if status is ACTIVE */}
          {!isOutOfStock && (
            <p className="mt-2 text-sm text-gray-500">
              Price negotiable. Contact seller for bulk orders.
            </p>
          )}
        </div>

        {/* Product Description */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Package className="h-5 w-5 text-gray-600" />
            Product Description
          </h2>
          <p
            className="text-gray-700 leading-relaxed whitespace-pre-line"
            style={{ wordWrap: 'break-word' }}
          >
            {displayDescription}
          </p>

          {/* Read More/Less Button */}
          {shouldTruncate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              className="text-primary hover:text-primary/80 px-0"
            >
              {isDescriptionExpanded ? 'Read Less' : 'Read More'}
            </Button>
          )}
        </div>

        {/* Additional Information */}
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Location */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
              <p className="text-base text-gray-900">
                {product.location.district}, {product.location.state}
              </p>
            </div>

            {/* Inquiry Count (Optional) */}
            {product.inquiryCount !== undefined && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Inquiries Received</h3>
                <p className="text-base text-gray-900">
                  {product.inquiryCount} {product.inquiryCount === 1 ? 'inquiry' : 'inquiries'}
                </p>
              </div>
            )}

            {/* Posted Date */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Posted On</h3>
              <p className="text-base text-gray-900">
                {new Date(product.createdAt).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>

            {/* Status */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Availability</h3>
              <p className="text-base text-gray-900">
                {isOutOfStock ? (
                  <span className="text-red-600 font-medium">Currently Unavailable</span>
                ) : (
                  <span className="text-green-600 font-medium">Available for Inquiry</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Out of Stock Notice */}
        {isOutOfStock && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-900 mb-1">
                  Product Currently Unavailable
                </h4>
                <p className="text-sm text-red-700">
                  This product is currently out of stock. You can still submit an inquiry,
                  and the seller will notify you when it becomes available again.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductInfo;
